// Live notes (FR-M5 / FR-X4): how to get a live transcript in a call. Live notes are captured by
// the browser extension in the call's own tab (FR-M2a), never by this page, so the tab explains
// the extension instead of offering controls that don't record anything. The panel test harness
// that used to live here is at `liveNotesTestPath`, for Grackle staff only.

import type { ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Callout, Card, Icon } from "@grackle/ui";
import { AppShell } from "../components/AppShell";
import { PageLoader } from "../components/ui";
import MeetingsTabs from "../library/MeetingsTabs";
import { adminAiPath, liveNotesTestPath, meetingsPath } from "../library/routes";
import { EXTENSION_INSTALL_URL } from "../live/extension";
import { useIsPlatformAdmin } from "../ops/platformAdmin";
import { useSession } from "../session";
import "../library/meetings.css";
import "../live/live.css";

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="live-notes__step">
      <span className="live-notes__num" aria-hidden="true">
        {n}
      </span>
      <div className="live-notes__step-body">
        <h2 className="gk-h6">{title}</h2>
        {children}
      </div>
    </li>
  );
}

export default function LiveNotesPage() {
  const { slug } = useParams();
  const { orgs, user, loading } = useSession();
  const org = orgs.find((o) => o.slug === slug) ?? orgs[0];
  const isPlatformAdmin = useIsPlatformAdmin();

  if (loading) return <PageLoader label="Loading live notes…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  const isOrgAdmin = org.role === "owner" || org.role === "admin";

  return (
    <AppShell org={org} user={user} section="meetings">
      <div className="meetings">
        <div className="meetings__head">
          <div className="meetings__head-copy">
            <h1 className="gk-h2">Meetings</h1>
          </div>
        </div>

        <MeetingsTabs slug={org.slug} current="live" />

        <p className="gk-sm meetings__lead">
          Get a live transcript while a call is happening. Use the Grackle extension for Chrome or Edge in the
          browser tab where the call is open. Mark the moments that matter, and when the call ends, the recording and
          its transcript are saved to your library.
        </p>

        <div className="meetings__grid">
          <ol className="live-notes__steps">
            <Step n={1} title="Add the Grackle extension">
              <p className="gk-sm">
                {EXTENSION_INSTALL_URL ? (
                  <>
                    <a href={EXTENSION_INSTALL_URL} target="_blank" rel="noreferrer">
                      Add Grackle to Chrome
                    </a>
                    . It also works in Microsoft Edge.
                  </>
                ) : (
                  "Grackle for Chrome isn't in the Chrome Web Store yet. Ask your workspace admin how to install it."
                )}{" "}
                Stay signed in to Grackle in the same browser. The extension uses that sign-in and never stores a
                password.
              </p>
            </Step>
            <Step n={2} title="Join your call, then start recording">
              <p className="gk-sm">
                Open the call in a browser tab and click the Grackle icon in the toolbar. Then click{" "}
                <strong>Record this meeting</strong> (Google Meet) or <strong>Record this call</strong> (any other
                site). The first time, Grackle opens a tab to ask for your microphone: click <strong>Allow</strong>{" "}
                there, then click Record again.
              </p>
              <p className="gk-sm">
                Outside Google Meet, the browser asks once per site to let Grackle show its recording banner there.
                If you decline, recording still works.
              </p>
            </Step>
            <Step n={3} title="Follow the live transcript">
              <p className="gk-sm">
                A side panel opens next to the call and shows each line as it's spoken. On Google Meet, lines show
                the speaker's name. Click <strong>Mark moment</strong> to flag something to come back to, and pause
                or stop without leaving the call.
              </p>
            </Step>
            <Step n={4} title="Stop, and find it in your library">
              <p className="gk-sm">
                The recording is saved on your computer first, so stopping is instant, even if your connection
                drops. It uploads in the background and shows up in your library, where the finished transcript
                replaces the live one.
              </p>
            </Step>
          </ol>

          <div className="meetings__col">
            <Card size="sm" className="live-notes__aside">
              <div className="gk-overline">Good to know</div>
              <p className="gk-sm">
                <Icon name="record" size={16} />
                <span>
                  Everyone in the call is told it's being recorded. Your workspace may also ask you to announce it
                  before you start.
                </span>
              </p>
              <p className="gk-sm">
                <Icon name="microphone" size={16} />
                <span>Grackle records the call's tab and your microphone, never your camera.</span>
              </p>
              <p className="gk-sm">
                <Icon name="robot" size={16} />
                <span>
                  Using the Zoom or Teams desktop app, or rather not install anything? Send the notetaker from the{" "}
                  <Link to={meetingsPath(org.slug)}>Calendar</Link> tab. It joins Meet, Zoom and Teams calls.
                </span>
              </p>
            </Card>

            <Callout tone="info" icon="subtitles">
              The live transcript needs speech-to-text turned on for your workspace. If the side panel says it's
              unavailable, the recording still continues.{" "}
              {isOrgAdmin ? (
                <Link to={adminAiPath(org.slug)}>Check AI and models</Link>
              ) : (
                "Ask a workspace admin to turn it on."
              )}
            </Callout>

            {isPlatformAdmin ? (
              <p className="gk-caption">
                Grackle staff: <Link to={liveNotesTestPath(org.slug)}>test the live panel</Link> against this
                server without the extension.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
